import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, Link, VStack, Skeleton, useColorModeValue } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
// import useIssueUrl from 'lib/hooks/useIssueUrl';
import { copy } from 'lib/html-entities';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
// import PopupCookie from './PopupCookie';
import getApiVersionUrl from './utils/getApiVersionUrl';

const MAX_LINKS_COLUMNS = 4;

const FRONT_VERSION_URL = '#';
const FRONT_COMMIT_URL = '#';

const Footer = () => {
  const { data: backendVersionData } = useApiQuery('config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
    },
  });
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  // const issueUrl = useIssueUrl(backendVersionData?.backend_version);
  const logoColor = useColorModeValue('blue.600', 'white');

  const BLOCKSCOUT_LINKS = [
    {
      icon: 'edit' as const,
      iconSize: '16px',
      text: 'Submit an issue',
      url: '#',
    },
    {
      icon: 'social/canny' as const,
      iconSize: '20px',
      text: 'Feature request',
      url: '#',
    },
    {
      icon: 'social/git' as const,
      iconSize: '18px',
      text: 'Contribute',
      url: '#',
    },
    {
      icon: 'social/twitter' as const,
      iconSize: '18px',
      text: 'X (Twitter)',
      url: 'https://x.com/',
    },
    {
      icon: 'social/discord' as const,
      iconSize: '24px',
      text: 'Discord',
      url: '#',
    },
    {
      icon: 'brands/blockscout' as const,
      iconSize: '18px',
      text: 'All Chains',
      url: '#',
    },
    {
      icon: 'donate' as const,
      iconSize: '20px',
      text: 'Donate',
      url: '#',
    },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return (
        <Link href={FRONT_VERSION_URL} target="_blank">
          {config.UI.footer.frontendVersion}
        </Link>
      );
    }

    if (config.UI.footer.frontendCommit) {
      return (
        <Link href={FRONT_COMMIT_URL} target="_blank">
          {config.UI.footer.frontendCommit}
        </Link>
      );
    }

    return null;
  })();

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: ['footer-links'],
    queryFn: async () => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex gridArea={gridArea} flexWrap="wrap" columnGap={8} rowGap={6} mb={{ base: 5, lg: 10 }} _empty={{ display: 'none' }}>
        {!config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus />}
        <NetworkAddToWallet />
        <div className="flex gap-2">
          <Link href="https://x.com/" target="_blank">
            <svg width="30" height="30" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="15" cy="15.5" r="15" fill="#8BA1DD" />
              <path
                d="M6.46994 6.92773L13.0893 16.386L6.42773 24.0706H7.92606L13.7576 17.3405L18.4706 24.0706H23.5706L16.5784 14.0825L22.7792 6.92773H21.2809L15.9101 13.1243L11.5699 6.92773H6.46994ZM8.67523 8.10767H11.0177L21.3653 22.8907H19.0228L8.67523 8.10767Z"
                fill="white"
              />
            </svg>
          </Link>
          <Link href="https://t.me/" target="_blank">
            <svg width="30" height="30" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="15" cy="15.5" r="15" fill="#8BA1DD" />
              <path
                d="M4.67037 15.1961L9.16449 16.897L10.8337 22.523C10.9621 22.9155 11.3473 23.0463 11.6042 22.7847L14.0438 20.6913C14.3006 20.4296 14.6858 20.4296 14.9426 20.6913L19.4368 23.9622C19.6936 24.2239 20.2072 23.9622 20.2072 23.7005L23.5457 7.60758C23.6741 7.21507 23.2889 6.82256 22.9037 6.9534L4.67037 14.1494C4.15675 14.2803 4.15675 14.9345 4.67037 15.1961ZM10.5769 15.9811L19.3084 10.486C19.4368 10.3552 19.5652 10.6168 19.4368 10.7477L12.2462 17.5512C11.9894 17.8129 11.861 18.0745 11.7326 18.4671L11.4758 20.2988C11.4758 20.5604 11.0905 20.5604 11.0905 20.2988L10.1917 16.897C10.0633 16.6353 10.1917 16.112 10.5769 15.9811Z"
                fill="white"
              />
            </svg>
          </Link>
          <Link href="https://youtube.com/" target="_blank" className="size-[30px] bg-[#8BA1DD] rounded-full flex justify-center items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="22" height="22">
              <path
                d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z"
                fill="white"
              />
            </svg>
          </Link>
          <Link href="https://www.facebook.com/" target="_blank" className="size-[30px] bg-[#8BA1DD] rounded-full flex justify-center items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="22" height="22">
              <path
                d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256h0z"
                fill="white"
              />
            </svg>
          </Link>
          <Link href="https://www.instagram.com/" target="_blank" className="size-[30px] bg-[#8BA1DD] rounded-full flex justify-center items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="22" height="22">
              <path
                d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"
                fill="white"
              />
            </svg>
          </Link>
        </div>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback(
    (gridArea?: GridProps['gridArea']) => {
      return (
        <Box gridArea={gridArea}>
          <div className="flex gap-4 items-center mb-5">
            <Image src="/static/logo.png" alt="logo" width={48} height={48} />
            <Text className="font-bold text-2xl">Powered by Xvitesse Chain</Text>
          </div>
          <Link href="https://xvitesse.com" target="_blank" className="text-[15px] !text-[#8BA1DD]">
          xvitesse.com
          </Link>
          <Text mt={3} fontSize="xs">
          Xvitesse Chain a Block Explorer and Analytics platform for Xvitesse Chain Ecosystem a decentralized smart contract blockchain.
          </Text>
          <Box mt={6} alignItems="start" fontSize="xs" lineHeight={5}>
            <Text>
            Xvitesse Chain © {new Date().getFullYear()} | Built by <b>Xvitesse Chain Team</b>
            </Text>
          </Box>
        </Box>
      );
    },
    [apiVersionUrl, backendVersionData?.backend_version, frontendLink, logoColor]
  );

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'solid',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${CONTENT_MAX_WIDTH}px`,
    m: '0 auto',
  };

  if (config.UI.footer.links) {
    return (
      <Box {...containerProps}>
        <Grid {...contentProps}>
          <div>
            {renderNetworkInfo()}
            {renderProjectInfo()}
          </div>

          <Grid
            gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, 160px)',
              lg: `repeat(${colNum}, 135px)`,
              xl: `repeat(${colNum}, 160px)`,
            }}
            justifyContent={{ lg: 'flex-end' }}
            mt={{ base: 8, lg: 0 }}
          >
            {[{ title: 'XVI', links: BLOCKSCOUT_LINKS }, ...(linksData || [])].slice(0, colNum).map((linkGroup) => (
              <Box key={linkGroup.title}>
                <Skeleton fontWeight={500} mb={3} display="inline-block" isLoaded={!isPlaceholderData}>
                  {linkGroup.title}
                </Skeleton>
                <VStack spacing={1} alignItems="start">
                  {linkGroup.links.map((link) => (
                    <FooterLinkItem {...link} key={link.text} isLoading={isPlaceholderData} />
                  ))}
                </VStack>
              </Box>
            ))}
          </Grid>
        </Grid>
        {/* <PopupCookie/> */}
      </Box>
    );
  }

  return (
    <Box {...containerProps}>
      <Grid
        {...contentProps}
        gridTemplateAreas={{
          lg: `
          "network links-top"
          "info links-bottom"
        `,
        }}
      >
        {renderNetworkInfo({ lg: 'network' })}
        {renderProjectInfo({ lg: 'info' })}

        <Grid
          gridArea={{ lg: 'links-bottom' }}
          rowGap="20px"
          columnGap={1}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 160px)',
            lg: 'repeat(3, 160px)',
            xl: 'repeat(3, 160px)',
          }}
          gridTemplateRows={{
            base: 'auto',
            lg: 'repeat(3, auto)',
            xl: 'repeat(2, auto)',
          }}
          gridAutoFlow={{ base: 'row', lg: 'column' }}
          alignContent="start"
          justifyContent={{ lg: 'flex-end' }}
          mt={{ base: 8, lg: 0 }}
        >
          {BLOCKSCOUT_LINKS.map((link) => (
            <FooterLinkItem {...link} key={link.text} />
          ))}
        </Grid>
      </Grid>
      {/* <PopupCookie/> */}
    </Box>
  );
};

export default React.memo(Footer);
